import { useEffect, useRef, useState } from "react";
import { Loader2, Search } from "lucide-react";
import api from "../../../../shared/services/api";
import ConversaItem from "./ConversaItem";

const FILTROS = [
  { id: "todas",         label: "Todas" },
  { id: "nao-lidas",     label: "Não lidas" },
  // Última mensagem é do cliente: nem agente, nem automação, nem humano responderam.
  { id: "sem-resposta",  label: "Sem resposta" },
  { id: "responderam",   label: "Responderam" },
  { id: "atendimento",   label: "Atendimento" },
  { id: "janela-aberta", label: "Janela aberta" },
];

const LIMIT = 20;

function useDebounce(valor, delay) {
  const [debounced, setDebounced] = useState(valor);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(valor), delay);
    return () => clearTimeout(timer);
  }, [valor, delay]);
  return debounced;
}

export default function ConversasList({ contaId, onSelect, selectedId, socket, modoEvent }) {
  const [conversas, setConversas]     = useState([]);
  const [cursor, setCursor]           = useState(null);
  const [hasMore, setHasMore]         = useState(false);
  const [loading, setLoading]         = useState(true);
  const [loadingMais, setLoadingMais] = useState(false);
  const [filtro, setFiltro]           = useState("todas");
  const [busca, setBusca]             = useState("");

  const buscaDebounced = useDebounce(busca, 300);
  // Mantém ref ao selectedId atual para uso dentro do closure do socket
  const selectedIdRef = useRef(selectedId);
  useEffect(() => { selectedIdRef.current = selectedId; }, [selectedId]);
  // Filtro/busca também via ref: o handler do socket é criado uma vez
  // ([socket, contaId]) e chamava carregar() com o filtro do primeiro render
  // ("todas") — numa aba filtrada vazia, qualquer mensagem nova recarregava a
  // lista inteira sem filtro (bug visto na aba Atendimento).
  const filtroRef = useRef(filtro);
  const buscaRef  = useRef(buscaDebounced);
  useEffect(() => { filtroRef.current = filtro; }, [filtro]);
  useEffect(() => { buscaRef.current = buscaDebounced; }, [buscaDebounced]);
  // Throttle do reset quando chega mensagem de conversa NOVA não cacheada.
  // Sem isso, um burst de mensagens de conversas diferentes dispara N reloads.
  const ultimoResetRef = useRef(0);

  async function carregar({ reset = false, cursorOverride = undefined } = {}) {
    const cursorParam = cursorOverride !== undefined ? cursorOverride : (reset ? null : cursor);

    if (reset) setLoading(true);
    else setLoadingMais(true);

    try {
      const filtroAtual = filtroRef.current;
      const buscaAtual  = buscaRef.current;
      const params = { limit: LIMIT, filtro: filtroAtual };
      if (cursorParam) params.cursor = cursorParam;
      if (buscaAtual.trim()) params.busca = buscaAtual.trim();

      const resp = await api.get(`/whatsapp/meta/contas/${contaId}/conversas`, { params });
      const payload    = resp.data?.data ?? resp.data ?? {};
      const novas      = payload.conversas ?? payload.data ?? [];
      const proxCursor = payload.cursor ?? null;
      const temMais    = payload.hasMore ?? (novas.length === LIMIT);

      // Usuário trocou de aba/busca enquanto esta request estava em voo — descarta.
      if (filtroRef.current !== filtroAtual || buscaRef.current !== buscaAtual) return;

      if (reset) {
        setConversas(novas);
      } else {
        setConversas((prev) => [...prev, ...novas]);
      }
      setCursor(proxCursor);
      setHasMore(temMais);
    } catch (_err) {
      // erro silencioso — componente exibe estado vazio
    } finally {
      setLoading(false);
      setLoadingMais(false);
    }
  }

  // Assumir/encerrar atendimento no ChatView: reflete na lista na hora.
  // Na aba Atendimento, encerrar tira a conversa da lista; nas outras abas só
  // atualiza o campo modo do item.
  useEffect(() => {
    if (!modoEvent) return;
    setConversas((prev) => {
      if (filtroRef.current === "atendimento" && modoEvent.modo === "bot") {
        return prev.filter((c) => c.id !== modoEvent.id);
      }
      return prev.map((c) => (c.id === modoEvent.id ? { ...c, modo: modoEvent.modo } : c));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modoEvent]);

  // Recarrega do zero quando contaId, filtro ou busca debounced mudam
  useEffect(() => {
    carregar({ reset: true, cursorOverride: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contaId, filtro, buscaDebounced]);

  // Socket.IO — real-time
  useEffect(() => {
    if (!socket || !contaId) return;

    socket.emit("join-conversas", contaId);

    function handleMensagemNew(payload) {
      if (!payload || payload.conta_id !== contaId) return;

      setConversas((prev) => {
        const idx = prev.findIndex((c) => c.id === payload.conversa_id);

        // Aba "Sem resposta": quem acabou de ser respondido sai da lista.
        if (filtroRef.current === "sem-resposta" && payload.mensagem?.direcao === "out") {
          return idx === -1 ? prev : prev.filter((_, i) => i !== idx);
        }

        if (idx === -1) {
          // Conversa nova ainda não na lista — recarrega sem bloquear o closure.
          // Throttle de 2s: bursts de várias conversas novas viram 1 reload só.
          const agora = Date.now();
          if (agora - ultimoResetRef.current >= 2000) {
            ultimoResetRef.current = agora;
            setTimeout(() => carregar({ reset: true, cursorOverride: null }), 0);
          }
          return prev;
        }

        const antiga = prev[idx];
        const atualizada = {
          ...antiga,
          ultimo_inbound_em: payload.mensagem?.data_criacao ?? new Date().toISOString(),
          ultima_mensagem: {
            ...antiga.ultima_mensagem,
            snippet:
              payload.mensagem?.conteudo?.text?.body ??
              payload.mensagem?.conteudo?.caption ??
              antiga.ultima_mensagem?.snippet ??
              "",
            data:    payload.mensagem?.data_criacao ?? new Date().toISOString(),
            direcao: payload.mensagem?.direcao ?? "in",
          },
          // Incrementa unread só se o chat não está aberto agora
          unread_count:
            selectedIdRef.current === payload.conversa_id
              ? (antiga.unread_count ?? 0)
              : (antiga.unread_count ?? 0) + 1,
          // Reabre janela se for mensagem inbound
          janela_expira_em:
            payload.mensagem?.direcao === "in"
              ? new Date(Date.now() + 24 * 3600 * 1000).toISOString()
              : antiga.janela_expira_em,
        };

        // Bumpa pro topo
        const restante = prev.filter((_, i) => i !== idx);
        return [atualizada, ...restante];
      });
    }

    // Reconexão cria socket novo no servidor (rooms perdidas) — re-joina e
    // recarrega a lista pra reconciliar eventos perdidos durante a queda.
    function handleReconnect() {
      socket.emit("join-conversas", contaId);
      const agora = Date.now();
      if (agora - ultimoResetRef.current >= 2000) {
        ultimoResetRef.current = agora;
        carregar({ reset: true, cursorOverride: null });
      }
    }

    socket.on("connect",      handleReconnect);
    socket.on("mensagem:new", handleMensagemNew);

    return () => {
      socket.off("connect",      handleReconnect);
      socket.off("mensagem:new", handleMensagemNew);
      socket.emit("leave-conversas", contaId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, contaId]);

  return (
    <div className="flex flex-col h-full bg-base">
      {/* Busca */}
      <div className="px-3 pt-3 pb-2 border-b border-white/[0.06]">
        <div className="relative">
          <Search
            className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-ink-5 pointer-events-none"
            aria-hidden="true"
          />
          <input
            type="search"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar por nome ou telefone..."
            aria-label="Buscar conversa por nome ou telefone"
            className="w-full bg-surface-1 border border-white/[0.06] rounded text-xs text-ink-2 pl-8 pr-3 py-1.5 placeholder:text-ink-5 focus:outline-none focus:border-brand-600 transition-colors"
          />
        </div>
      </div>

      {/* Filtros */}
      <div
        role="group"
        aria-label="Filtrar conversas"
        className="flex flex-wrap items-center gap-1 px-3 py-2 border-b border-white/[0.06]"
      >
        {FILTROS.map(({ id, label }) => (
          <button
            key={id}
            type="button"
            onClick={() => setFiltro(id)}
            aria-pressed={filtro === id}
            aria-label={`Filtrar: ${label}`}
            className={`px-2.5 py-1 rounded text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ${
              filtro === id
                ? "bg-brand-600 text-white"
                : "text-ink-3 hover:text-ink-2 hover:bg-surface-2"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Lista */}
      <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-track-zinc-900 scrollbar-thumb-zinc-700">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-5 h-5 animate-spin text-ink-5" />
          </div>
        ) : conversas.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
            <p className="text-sm text-ink-4">
              {buscaDebounced.trim()
                ? "Nenhuma conversa encontrada para essa busca"
                : filtro !== "todas"
                ? "Nenhuma conversa neste filtro"
                : "Nenhuma conversa ainda"}
            </p>
          </div>
        ) : (
          <>
            {conversas.map((c) => (
              <ConversaItem
                key={c.id}
                conversa={c}
                selected={selectedId === c.id}
                onClick={() => onSelect(c.id)}
              />
            ))}

            {hasMore && (
              <div className="px-3 py-3">
                <button
                  type="button"
                  onClick={() => carregar()}
                  disabled={loadingMais}
                  aria-label="Carregar mais conversas"
                  className="w-full text-xs text-ink-3 hover:text-ink-2 bg-surface-1 hover:bg-surface-2 border border-white/[0.06] rounded py-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:opacity-50"
                >
                  {loadingMais ? (
                    <span className="inline-flex items-center gap-2 justify-center">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      Carregando...
                    </span>
                  ) : (
                    "Carregar mais"
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
